import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import { CardLayout, DeleteModal, MTable } from "../v3";
import { getStatementTableColumns } from "../../pages/employee/constants";
import { AccountService, FinanceService, HumanResourceService } from "../../services";
import {
  DELETE_EMPLOYEE_ATTENDANCE,
  DELETE_EMPLOYEE_SALARY,
  EDIT_EMPLOYEE_ATTENDANCE,
  EDIT_EMPLOYEE_SALARY,
  EXPORT_EMPLOYEE_STATEMENT,
} from "../../constants";
import { get_date } from "../generalComponets";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { getAccountName } from "../../utils/formatters";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const EmployeeStatement = ({ showFilterButton = false, onSuccess = () => {}, reloadData = 0 }) => {
  const [statements, setStatements] = useState([]);
  const [accounts, setAccounts] = useState([]);
  // const [selectedFilter, setSelectedFilter] = useState("ALL");
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "statement",
    defaults: { sortModel: [{ field: "date", sort: "desc" }] },
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    statementId: null,
    type: undefined,
  });

  const { id: employeeId } = useParams();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.user);

  const hasEditAttendance = useHasPermission(EDIT_EMPLOYEE_ATTENDANCE);
  const hasDeleteAttendance = useHasPermission(DELETE_EMPLOYEE_ATTENDANCE);
  const hasEditSalary = useHasPermission(EDIT_EMPLOYEE_SALARY);
  const hasDeleteSalary = useHasPermission(DELETE_EMPLOYEE_SALARY);
  const hasExportStatement = useHasPermission(EXPORT_EMPLOYEE_STATEMENT);

  const fetchData = useCallback(
    async (statementFilers = {}) => {
      setLoading(true);
      try {
        const employeeResponse = await HumanResourceService.getEmployeeById(employeeId);
        const employeeAccountId = employeeResponse?.data?.accountId;

        const transactionsResponse = await FinanceService.getTransactions({
          ...statementFilers,
          toAccountId: employeeAccountId,
        });
        setStatements(transactionsResponse?.data?.rows);
        setTotalCount(transactionsResponse?.data?.count);

        const accountIds = Array.from(new Set(transactionsResponse?.data?.rows?.map((item) => item.fromAccountId)));

        if (accountIds.length !== 0) {
          const accountResponse = await AccountService.getAccounts({
            ids: accountIds.join(","),
            limit: accountIds.length,
            includeDeleted: true,
            includeOwnerDetails: true,
          });
          setAccounts(accountResponse?.data?.rows);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching data:", error);
      }
    },
    [employeeId],
  );

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput default/apply callback

    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
    });
  }, [fetchData, sortModel, paginationState, dateRange, reloadData]);

  const handleFilterChange = (filterValue) => {
    // setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    // WHY: Normalize picker dates into inclusive ISO boundaries for statement filtering.
    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Employee Statement" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Employee Statement",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const onViewClick = (params, event) => {
    // handle view click
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      statementId: null,
    });
  };

  const handleEditTransaction = (transactionDetails) => {
    navigate(`/transaction/edit/${transactionDetails.id}?employeeId=${employeeId}`);
  };
  const handleEditAttendance = (attendanceDetails) => {};

  const handleDelete = async () => {
    try {
      if (deleteModal.type === "transaction") {
        await FinanceService.deleteTransaction(deleteModal.statementId);
        toast.success("Transaction deleted successfully");
        onSuccess?.();
      } /*else if (deleteModal.type === "attendance") {
        await HumanResourceService.deleteAttendance(deleteModal.statementId);
        toast.success("Attendance deleted successfully");
      }*/
    } catch (error) {
      toast.error(`Failed to delete ${deleteModal.type}`);
      console.error(`🚀 ~ deleteStatement ~ error:`, error);
    }
    closeDeleteModal();
  };

  const handleActions = async (statementData, actionType) => {
    if (actionType === "edit") {
      if (statementData.type) {
        // TODO: will be replaced with actual type once API is ready
        // update in statementColumnHeaders as well
        handleEditTransaction(statementData);
      } else {
        handleEditAttendance(statementData);
      }
    } else {
      setDeleteModal({
        isOpen: true,
        statementId: statementData.id,
        type: statementData.type ? "transaction" : "attendance", // TODO: will be replaced with actual type once API is ready
      });
    }
  };

  // const filterOptions = [
  //   { label: "All", value: "ALL", icon: <ModeStandbyOutlined /> },
  //   { label: "Work", value: "WORK", icon: <WorkOutlineOutlined /> },
  //   { label: "Payment", value: "PAYMENT", icon: <AttachMoneyOutlined /> },
  // ];

  const columnHeaders = getStatementTableColumns(
    handleActions,
    hasEditAttendance,
    hasDeleteAttendance,
    hasEditSalary,
    hasDeleteSalary,
  );

  const rows = statements.map((statement, index) => {
    const account = accounts.find((account) => account.id === statement.fromAccountId);
    return {
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      id: statement.id,
      date: get_date(statement.date),
      description: statement.description || "-",
      accountName: getAccountName(account?.type, account) || "-",
      amount: statement.amount,
      type: statement.type,
    };
  });

  const DEFAULT_SORT = [{ field: "date", sort: "desc" }];

  return (
    <>
      <CardLayout>
        <MTable
          // Filter data
          // selectedFilter={selectedFilter}
          // filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          showFilterButton={showFilterButton}
          // Search data
          placeholder="Search"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          // showSearchBar
          // Date range data
          showDateRangePicker
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          // Export data
          showExportButton={hasExportStatement}
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          defaultSortModel={DEFAULT_SORT}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message={`Want to delete this 
          ${deleteModal.type === "transaction" ? "Transaction" : "Attendance"}?`}
      />
    </>
  );
};
